import axios from 'axios';

// Create axios instance with base URL
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api', // Use relative URL for development
  headers: {
    'Content-Type': 'application/json',
  },
  // Set a longer timeout for slow connections
  timeout: 15000,
  // Enable credentials for CORS (if needed)
  withCredentials: true,
});

// Add interceptor for authentication tokens
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('ncrs_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle authentication errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Clear invalid token and redirect to login
      localStorage.removeItem('ncrs_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API services
export const reportService = {
  // Submit a new report
  submitReport: async (formData) => {
    // If formData is already a FormData object, use it directly
    if (formData instanceof FormData) {
      return apiClient.post('/reports', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
    }

    // Legacy support: if formData is an object with separate files parameter
    const { reportData, files } = formData;
    const newFormData = new FormData();

    // Append all report data
    Object.keys(reportData).forEach(key => {
      newFormData.append(key, reportData[key]);
    });

    // Append all files
    if (files && files.length > 0) {
      files.forEach(file => {
        newFormData.append('files[]', file);
      });
    }

    return apiClient.post('/reports', newFormData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
  },

  // Get report by reference ID
  getReport: async (referenceId) => {
    return apiClient.get(`/reports/${referenceId}`);
  },

  // Archive a report
  archiveReport: async (referenceId) => {
    return apiClient.post(`/reports/${referenceId}/archive`);
  },

  // Export report as PDF
  exportReportPdf: async (referenceId) => {
    const response = await apiClient.get(`/reports/${referenceId}/export-pdf`, {
      responseType: 'blob'
    });
    return response;
  }
};

export const authService = {
  // Login
  login: async (credentials) => {
    const response = await apiClient.post('/auth/login', credentials);
    if (response.data?.success && response.data?.data?.token) {
      localStorage.setItem('ncrs_token', response.data.data.token);
      return response.data;
    } else {
      throw new Error(response.data?.message || 'Login failed');
    }
  },

  // Logout
  logout: async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('ncrs_token');
    }
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    const token = localStorage.getItem('ncrs_token');
    if (!token) return false;

    try {
      // Decode the token to check if it's valid format
      const decoded = atob(token);
      const parts = decoded.split('|');
      return parts.length >= 3; // userId|email|timestamp
    } catch (error) {
      localStorage.removeItem('ncrs_token');
      return false;
    }
  },

  // Get current user info from token
  getCurrentUser: () => {
    const token = localStorage.getItem('ncrs_token');
    if (!token) return null;

    try {
      const decoded = atob(token);
      const parts = decoded.split('|');
      if (parts.length >= 3) {
        return {
          id: parts[0],
          email: parts[1],
          timestamp: parts[2]
        };
      }
      return null;
    } catch (error) {
      return null;
    }
  }
};

export const dashboardService = {
  // Get all reports for dashboard
  getReports: async (filters = {}) => {
    return apiClient.get('/dashboard/reports', { params: filters });
  },

  // Get dashboard statistics
  getStatistics: async () => {
    return apiClient.get('/dashboard/statistics');
  },

  // Update report status
  updateReportStatus: async (reportId, status, notes = '') => {
    return apiClient.patch(`/dashboard/reports/${reportId}`, {
      status,
      notes
    });
  },

  // Export reports to CSV
  exportReports: async (filters = {}) => {
    return apiClient.get('/dashboard/export', {
      params: filters,
      responseType: 'blob'
    });
  }
};

// Create a services object
const services = {
  reportService,
  authService,
  dashboardService
};

export default services;
